"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Bell,
  CalendarDays,
  LayoutDashboard,
  ListChecks,
  Menu,
  Settings,
  LogOut,
  User,
  HelpCircle,
  Search,
  Building,
  ChevronDown,
  Users,
  Video,
} from "lucide-react";
import { signOut } from "next-auth/react";
import Image from 'next/image';

function clearVideoEditorStorage() {
  try {
    localStorage.removeItem('videoEditorDraft');
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith('videoEditor_')) localStorage.removeItem(key);
    }
  } catch {}
  try {
    const req = indexedDB.deleteDatabase('VideoEditorDB');
    req.onerror = () => {};
  } catch {}
}

function handleLogout() {
  clearVideoEditorStorage();
  signOut({ callbackUrl: '/login' });
}

function hasNextAuthSessionCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((part) => {
    const name = part.trim().split("=")[0];
    return (
      name === "next-auth.session-token" ||
      name.startsWith("next-auth.session-token.") ||
      name === "__Secure-next-auth.session-token" ||
      name.startsWith("__Secure-next-auth.session-token.")
    );
  });
}

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/auth-context";
import { useHasOpenJobs } from "@/hooks/use-has-open-jobs";
import { NotificationDropdown } from "@/components/notifications/notification-dropdown";
import { isAdmin, isAdvertiser, isEmployee, isTutor, isStudent, isCandidate } from "@/lib/role-utils";
import { getDashboardPath } from "@/lib/dashboard-path";
import { JOB_BOARD_ENABLED } from "@/constants/feature-flags";
import { FOOTER_LEGAL_LINKS, getFooterLinkGroups } from "@/lib/footer-links";
import { AppDownloadLink } from "@/components/app-download-link";
import { ANDROID_PLAY_STORE_URL } from "@/constants/app-store";
import { FoundingMemberBadge, UserAvatar } from "@/components/founding-member-badge";
import { getAvatarFallback } from "@/lib/avatar-fallback";
import {
  tourTargetForHref,
  OPEN_MOBILE_MENU_EVENT,
  CLOSE_MOBILE_MENU_EVENT,
} from "@/constants/feature-tour";


export default function Navbar(): JSX.Element {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  // All functions in this file now have explicit return types.

  const onboardingUpper = (user?.onboardingStatus as string)?.toUpperCase?.();
  const isTutorUnapproved = user &&
    isTutor(user) &&
    (!onboardingUpper ||
    ["PENDING", "IN_PROGRESS", "SUBMITTED", "UNDER_REVIEW"].includes(onboardingUpper));

  const isAdvertiserUser = isAdvertiser(user);
  const isAdvertiserUnapproved = isAdvertiserUser && user?.advertiserProfile && user?.advertiserProfile.status !== "APPROVED";

  const isAdminUser = isAdmin(user);
  const isEmployeeUser = isEmployee(user);

  const getUserInitials = (u: typeof user) =>
    getAvatarFallback({
      name: u?.name,
      firstName: (u as { firstName?: string } | null)?.firstName,
      lastName: (u as { lastName?: string } | null)?.lastName,
      username: (u as { username?: string } | null)?.username,
    });

  const logoHref = isAuthenticated && user ? getDashboardPath(user.role) : "/";

  // Profile URL for students/tutors — never fall back to the email local-part.
  function getProfileHref(u: typeof user): string {
    if (!u) return "/feed";
    const username = (u as any).username;
    if (username) return `/social-profile/${encodeURIComponent(username)}`;
    return "/account/profile";
  }

  // Helper function to get user type
  function getUserType(user: Record<string, unknown> | null | undefined): string {
    if (!user) return "unauthenticated";
    
    // Check roles using utility functions
    if (isAdvertiser(user)) return "advertiser";
    if (isTutor(user)) return "tutor";
    if (isStudent(user)) return "student";
    if (isEmployee(user) && isAdmin(user)) return "employee+admin";
    if (isEmployee(user)) return "employee";
    if (isAdmin(user)) return "admin";
    
    return "unauthenticated";
  }

  // Guest conversion nav. Authenticated roles keep their own dashboards and tools.
  const navLinks: Record<string, { name: string; href: string }[]> = {
    unauthenticated: [
      { name: "Feed", href: "/feed" },
      { name: "Certifications", href: "/certifications" },
      { name: "Free Study Planner", href: "/free-study-planner" },
      { name: "Study Groups", href: "/study-groups" },
      { name: "Forums", href: "/forums" },
      { name: "Tutors", href: "/tutors" },
    ],
    employee: [
      { name: "Employee Dashboard", href: "/employee/dashboard" },
      { name: "Feed", href: "/feed" },
      { name: "Tutors", href: "/tutors" },
      { name: "Study Groups", href: "/study-groups" },
      { name: "Create Study Plan", href: "/study-plans" },
    ],
    admin: [
      { name: "Admin Dashboard", href: "/admin/dashboard" },
      { name: "Feed", href: "/feed" },
      { name: "Forums", href: "/forums" },
      { name: "Tutors", href: "/tutors" },
      { name: "Study Groups", href: "/study-groups" },
    ],
    "employee+admin": [
      { name: "Employee Dashboard", href: "/employee/dashboard" },
      { name: "Admin Dashboard", href: "/admin/dashboard" },
      { name: "Feed", href: "/feed" },
      { name: "Tutors", href: "/tutors" },
      { name: "Study Groups", href: "/study-groups" },
    ],
    student: [
      { name: "Student Dashboard", href: "/student" },
      { name: "Study Plans", href: "/study-plans" },
      { name: "Feed", href: "/feed" },
      { name: "Forums", href: "/forums" },
      { name: "Tutors", href: "/tutors" },
      { name: "Study Groups", href: "/study-groups" },
      { name: "Video Studio", href: "/videos" },
    ],
    tutor: [
      { name: "Tutor Dashboard", href: "/tutor" },
      { name: "Feed", href: "/feed" },
      { name: "Forums", href: "/forums" },
      { name: "Tutors", href: "/tutors" },
      { name: "Video Studio", href: "/videos" },
    ],
    advertiser: [
      { name: "Advertiser Dashboard", href: "/advertiser/dashboard" },
    ],
  };

  const userType = getUserType(user);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hasOpenJobs = useHasOpenJobs();
  const footerGroups = getFooterLinkGroups({
    hideStudyGroups: isTutor(user),
    jobBoardEnabled: JOB_BOARD_ENABLED,
    hasOpenJobs,
  });
  const rawLinks = navLinks[userType] || navLinks["unauthenticated"];
  const links = rawLinks
    .filter((item) => !(isTutorUnapproved && item.href === "/videos"))
    .map((item) => {
      // Guests keep the public planner. Any signed-in nav that still points
      // there (including the guest link set) goes to the saved-plan creator.
      if (isAuthenticated && item.href === "/free-study-planner") {
        return { name: "Create Study Plan", href: "/study-plans" };
      }
      return {
        ...item,
        href: item.href === "__PROFILE__" ? (user ? getProfileHref(user) : "/feed") : item.href,
      };
    });
  // Guests have no session cookie — show Login/Sign Up immediately so the
  // loading avatar skeleton never flashes on the homepage. Signed-in users
  // keep the skeleton until NextAuth resolves (cookie check runs before paint).
  const [awaitingSession, setAwaitingSession] = useState(false);
  const showAvatarSkeleton = authLoading && awaitingSession;

  useLayoutEffect(() => {
    setAwaitingSession(hasNextAuthSessionCookie());
  }, []);

  useEffect(() => {
    const open = () => setMobileMenuOpen(true);
    const close = () => setMobileMenuOpen(false);
    window.addEventListener(OPEN_MOBILE_MENU_EVENT, open);
    window.addEventListener(CLOSE_MOBILE_MENU_EVENT, close);
    return () => {
      window.removeEventListener(OPEN_MOBILE_MENU_EVENT, open);
      window.removeEventListener(CLOSE_MOBILE_MENU_EVENT, close);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) return;
    const id = window.setTimeout(() => {
      if (document.body.style.pointerEvents === "none" && !document.querySelector("[role='dialog']")) {
        document.body.style.pointerEvents = "";
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [mobileMenuOpen]);

  const isNavActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  const navLinkClass = (href: string, compact = false) =>
    `block rounded-md font-semibold whitespace-nowrap ${compact ? "px-2.5 py-2 text-sm" : "px-4 py-3 text-base"} ${isNavActive(href) ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-100"}`;

  return (
    <nav className="w-full bg-white border-b sticky top-0 z-50 print:hidden" data-print-hide>
      <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between h-12 lg:h-16 min-w-0 gap-2">
        {/* Left: hamburger, logo, desktop nav links */}
        <div className="flex items-center gap-1 sm:gap-2 md:gap-8 min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="lg:hidden h-9 w-9 shrink-0"
            data-tour="nav-menu"
            aria-label="Open menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          {mobileMenuOpen && typeof document !== "undefined" && createPortal(
            <div className="lg:hidden" data-mobile-menu>
              <button
                type="button"
                aria-label="Close menu"
                className="fixed inset-0 z-[200] bg-black/50"
                onClick={() => setMobileMenuOpen(false)}
              />
              <div className="fixed inset-y-0 left-0 z-[201] w-[min(100%,20rem)] overflow-y-auto bg-white p-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                  <button
                    type="button"
                    className="rounded-md px-2 py-1 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Close
                  </button>
                </div>
              <nav className="flex flex-col gap-1 mt-6">
                {links.map((item) => (
                  <Link
                    key={item.name}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={navLinkClass(item.href)}
                    data-tour={tourTargetForHref(item.href)}
                  >
                    {item.name}
                  </Link>
                ))}
                {!isAuthenticated && (
                  <>
                    <div className="border-t my-2" />
                    <Link href="/login" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-md font-semibold text-base text-blue-600 border border-blue-600 hover:bg-blue-50 text-center">
                      Login
                    </Link>
                    <Link href="/register" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-md font-semibold text-base bg-blue-600 text-white hover:bg-blue-700 text-center">
                      Sign Up
                    </Link>
                  </>
                )}

                {/* Footer links live here on mobile (the site footer is desktop-only). */}
                <div className="border-t my-2" />
                <details className="group">
                  <summary className="flex items-center justify-between px-4 py-2.5 rounded-md text-xs font-semibold uppercase tracking-wide text-gray-400 cursor-pointer list-none hover:bg-gray-100">
                    More
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 group-open:rotate-180" />
                  </summary>
                  <div className="flex flex-col gap-1 mt-1 pb-4">
                    {footerGroups
                      .filter((group) => group.items.length > 0)
                      .map((group) => (
                      <div key={group.heading}>
                        <p className="px-4 pt-3 pb-1 text-sm font-bold text-gray-900">
                          {group.heading}
                        </p>
                        {group.items.map((item) => (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block px-4 py-2.5 rounded-md text-sm text-blue-600 hover:bg-blue-50"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    ))}
                    <div>
                      <p className="px-4 pt-3 pb-1 text-sm font-bold text-gray-900">
                        Legal
                      </p>
                      {FOOTER_LEGAL_LINKS.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-2.5 rounded-md text-sm text-blue-600 hover:bg-blue-50"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                    <AppDownloadLink
                      href={ANDROID_PLAY_STORE_URL}
                      store="google_play"
                      className="mx-4 mt-3 inline-block w-fit"
                    >
                      <Image
                        src="/google-play-badge.png"
                        alt="Get it on Google Play"
                        width={646}
                        height={250}
                        className="h-16 w-auto"
                      />
                    </AppDownloadLink>
                  </div>
                </details>
              </nav>
              </div>
            </div>,
            document.body
          )}
          <Link href={logoHref} className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2 min-w-0">
            <Image
              src="/certlynx-mark.png"
              alt=""
              width={213}
              height={150}
              className="h-8 w-auto sm:h-8 lg:h-9"
              priority
            />
            <span className="text-xl sm:text-xl lg:text-[1.75rem] font-semibold tracking-tight text-gray-900 leading-none">
              CertLynx
            </span>
          </Link>
          {/* Desktop: inline nav links — collapsed into the sheet below lg */}
          <div className="hidden lg:flex items-center gap-1">
            {links.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={navLinkClass(item.href, true)}
                data-tour={tourTargetForHref(item.href)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
        {/* Right: Search, Notifications, Profile */}
        <div className="flex items-center justify-end gap-1 sm:gap-2 shrink-0">
            {/* Search / Command Palette trigger */}
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full flex h-9 w-9"
              onClick={() => {
                window.dispatchEvent(new Event("certlynx:open-search"));
              }}
              aria-label="Search"
              title="Search"
            >
              <Search className="h-4 w-4" />
            </Button>
            {/* Notifications */}
            {isAuthenticated && (
              <div>
                <NotificationDropdown className="h-9 w-9" />
              </div>
            )}

            {/* Auth Buttons / User Menu — avatar skeleton only when a session cookie exists */}
            {showAvatarSkeleton ? (
              <div className="h-8 w-8 rounded-full bg-gray-100 animate-pulse" aria-hidden />
            ) : isAuthenticated && user ? (
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="relative h-8 w-8 rounded-full p-0 hover:bg-gray-200 touch-manipulation overflow-visible"
                    >
                      {(user as { image?: string })?.image ? (
                        <UserAvatar
                          className="h-8 w-8 rounded-full ring-2 ring-blue-600"
                          src={(user as { image?: string }).image}
                          alt={user.name || "Profile"}
                          fallback={getUserInitials(user)}
                          fallbackClassName="bg-gray-100 text-gray-700 text-xs"
                          isFoundingMember={Boolean((user as { isFoundingMember?: boolean }).isFoundingMember)}
                          badgeSize="sm"
                          badgeClassName="-left-0.5 right-auto"
                        />
                      ) : (
                        <span className="relative h-8 w-8 rounded-full ring-2 ring-blue-600 bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-700">
                          {getUserInitials(user) || <User className="h-4 w-4 text-gray-700" />}
                          {Boolean((user as { isFoundingMember?: boolean }).isFoundingMember) && (
                            <FoundingMemberBadge size="sm" className="absolute -bottom-0.5 -left-0.5" />
                          )}
                        </span>
                      )}
                      <span
                        className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white"
                        aria-label="Online"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 md:w-56 w-48 max-w-[90vw]" align="end" forceMount sideOffset={8}>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.name || "User"}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {userType !== "advertiser" && (
                    <DropdownMenuItem asChild>
                      <Link href="/find-people">
                        <Users className="mr-2 h-4 w-4" />
                        Find people
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {/* Profile dropdown links by userType */}
                  {(() => {
                    switch (userType) {
                      case "employee":
                        return <>
                          <DropdownMenuItem asChild>
                            <Link href="/employee/dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Employee Dashboard</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/calendar"><CalendarDays className="mr-2 h-4 w-4" />Calendar</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/messages"><Bell className="mr-2 h-4 w-4" />Messages</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/support"><HelpCircle className="mr-2 h-4 w-4" />Support</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/account/settings"><Settings className="mr-2 h-4 w-4" />Account Settings</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
                        </>;
                      case "admin":
                        return <>
                          <DropdownMenuItem asChild>
                            <Link href={getProfileHref(user)}><User className="mr-2 h-4 w-4" />Profile</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/admin/dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Admin Dashboard</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/messages"><Bell className="mr-2 h-4 w-4" />Messages</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/support"><HelpCircle className="mr-2 h-4 w-4" />Support</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/account/settings"><Settings className="mr-2 h-4 w-4" />Account Settings</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
                        </>;
                      case "employee+admin":
                        return <>
                          <DropdownMenuItem asChild>
                            <Link href={getProfileHref(user)}><User className="mr-2 h-4 w-4" />Profile</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/employee/dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Employee Dashboard</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/admin/dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Admin Dashboard</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/messages"><Bell className="mr-2 h-4 w-4" />Messages</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/support"><HelpCircle className="mr-2 h-4 w-4" />Support</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/account/settings"><Settings className="mr-2 h-4 w-4" />Account Settings</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
                        </>;
                      case "student":
                        return <>
                          <DropdownMenuItem asChild>
                            <Link href={getProfileHref(user)}><User className="mr-2 h-4 w-4" />Profile</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/student"><LayoutDashboard className="mr-2 h-4 w-4" />Student Dashboard</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/study-plans"><ListChecks className="mr-2 h-4 w-4" />Study plans</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/calendar"><CalendarDays className="mr-2 h-4 w-4" />Calendar</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/videos"><Video className="mr-2 h-4 w-4" />Video Studio</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/messages"><Bell className="mr-2 h-4 w-4" />Messages</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/support"><HelpCircle className="mr-2 h-4 w-4" />Support</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/account/settings"><Settings className="mr-2 h-4 w-4" />Account Settings</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
                        </>;
                      case "tutor":
                        return <>
                          <DropdownMenuItem asChild>
                            <Link href={getProfileHref(user)}><User className="mr-2 h-4 w-4" />Profile</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/tutor"><LayoutDashboard className="mr-2 h-4 w-4" />Tutor Dashboard</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/calendar"><CalendarDays className="mr-2 h-4 w-4" />Calendar</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/videos"><Video className="mr-2 h-4 w-4" />Video Studio</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/messages"><Bell className="mr-2 h-4 w-4" />Messages</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/support"><HelpCircle className="mr-2 h-4 w-4" />Support</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/account/settings"><Settings className="mr-2 h-4 w-4" />Account Settings</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
                        </>;
                      case "advertiser":
                        return <>
                          <DropdownMenuItem asChild>
                            <Link href="/advertiser/dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Advertiser Dashboard</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/support"><HelpCircle className="mr-2 h-4 w-4" />Support</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/settings"><User className="mr-2 h-4 w-4" />Account Settings</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/advertiser/dashboard?tab=settings"><Building className="mr-2 h-4 w-4" />Advertiser Settings</Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
                        </>;
                      default:
                        return null;
                    }
                  })()}
                                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="hidden lg:flex items-center space-x-2">
                <Button variant="ghost" className="professional-nav-item" asChild>
                  <Link href="/login">Login</Link>
                </Button>
                <Button className="professional-button-primary" asChild>
                  <Link href="/register">Sign Up</Link>
                </Button>
              </div>
            )}
        </div>
      </div>
    </nav>
  );
}